/**
 * Score Utility Functions
 * Shared logic for score normalization and color calculation
 */

export interface ScoreData {
  value: number;
  maxValue: number;
  percentage: number;
  color: string;
  label: string;
}

/**
 * Normalizes a score value to base 10
 * The API already returns scores in 0-10 scale, so we just return the value as-is
 * @param value - Score value from API (already in 0-10 scale)
 * @returns Score value (0-10)
 */
export const normalizeScore = (value: number): number => {
  return value;
};

/**
 * Calculates percentage from score value
 * @param scoreValue - Score value in 0-10 scale
 * @returns Percentage (0-100)
 */
export const calculatePercentage = (scoreValue: number): number => {
  return Math.round((scoreValue / 10) * 100);
};

/**
 * Determines color based on score percentage
 * @param percentage - Score percentage (0-100)
 * @returns Hex color code
 */
export const getScoreColor = (percentage: number): string => {
  if (percentage >= 80) return "#22c55e"; // green-500
  if (percentage >= 60) return "#3b82f6"; // blue-500
  if (percentage >= 40) return "#f59e0b"; // amber-500
  return "#ef4444"; // red-500
};

/**
 * Determines performance label based on score percentage
 * @param percentage - Score percentage (0-100)
 * @returns Performance label
 */
export const getScoreLabel = (percentage: number): string => {
  if (percentage >= 80) return "Excellent Performance";
  if (percentage >= 60) return "Good Performance";
  if (percentage >= 40) return "Fair Performance";
  return "Needs Improvement";
};

/**
 * Processes raw score data into normalized score with metadata
 * @param value - Raw score value from API
 * @param maxValue - Maximum possible score from API
 * @returns Complete score data object
 */
export const processScore = (value: number, maxValue: number): ScoreData => {
  const normalizedValue = normalizeScore(value);
  const percentage = calculatePercentage(normalizedValue);
  const color = getScoreColor(percentage);
  const label = getScoreLabel(percentage);

  return {
    value: normalizedValue,
    maxValue: 10,
    percentage,
    color,
    label,
  };
};
